import React from 'react';
import { Tooltip } from '@crystaldelta/loree-ui-components';
import { LintEditorFormatPropsType } from './lintEditorType';
import { EditorMenuButton, EditorMenuDropdown, EditorMenuDropdownButton } from './styledContents';
import {
  getFontName,
  getFontSize,
  isBulletList,
  isOrderedList,
} from '../editorExtensions/utilityFunctions';
import { ReactComponent as UnorderedListButton } from '../editorFormattingIcons/unorderedListButton.svg';
import { ReactComponent as DiscListButton } from '../editorFormattingIcons/discList.svg';
import { ReactComponent as CircleListButton } from '../editorFormattingIcons/circleList.svg';
import { ReactComponent as SquareListButton } from '../editorFormattingIcons/squareList.svg';
import { ReactComponent as OrderedListButton } from '../editorFormattingIcons/orderedListButton.svg';
import { ReactComponent as NumberListButton } from '../editorFormattingIcons/numberList.svg';
import { ReactComponent as AlphabetListButton } from '../editorFormattingIcons/alphabetList.svg';
import { ReactComponent as RomanListButton } from '../editorFormattingIcons/romanList.svg';
import { IconDiv } from './utilityFunctions';
import {
  checkMixedSelection,
  checkSelectionHasRestrictedNodes,
  determineListAction,
  flattenList,
} from './listUtilityFunctions';
import { updateNestedListStyle } from '../prosemirrorCustomCommands/nodeAttributesCommands';

export function ListElementOption(props: LintEditorFormatPropsType) {
  const { editor } = props;
  if (!editor) {
    return null;
  }

  const selectionHasRestrictedNodes = () => {
    return checkSelectionHasRestrictedNodes(editor);
  };

  const setList = (listType: 'bullet' | 'ordered', numberingType: string) => {

    const { from: fromOld, to: toOld } = editor.state.selection;
    editor.state.doc.nodesBetween(fromOld, toOld, (node: any, pos: number, parent: any) => {
      if (node.type.name === 'blockquote') {
        editor.commands.setTextSelection({
          from: editor.state.selection.$from.pos,
          to: editor.state.selection.$to.pos
        })
      }
      return false;
    });
    const state = editor.state;
    const { from, to } = state.selection;
    const isCursorOnly = from === to;

    const listNodeName = listType === 'bullet' ? 'bulletList' : 'orderedList';
    const toggleCommand =
      listType === 'bullet' ? 'toggleBulletListContent' : 'toggleOrderedListContent';
    const isBulletActive = editor.isActive('bulletList');
    const isOrderedActive = editor.isActive('orderedList');

    let activeType = null;

    switch (true) {
      case isBulletActive:
        activeType = 'bullet';
        break;
      case isOrderedActive:
        activeType = 'ordered';
        break;
    }


    let fontSize: any = getFontSize(editor);
    const size = fontSize?.size;
    const metric = fontSize?.unit;
    fontSize = `${size}${metric}`;
    const fontFamily = getFontName(editor);

    const updateListStyle = () =>
      editor.chain().focus().updateAttributes(listNodeName, { listStyleType: numberingType, style: `font-size: ${fontSize}; font-family: ${fontFamily};` }).run();

    const toggleList = () => editor.chain().focus()[toggleCommand](numberingType).run();



    const isMixedSelection = checkMixedSelection(editor);

    const flags = {
      isListActive: activeType === listType,
      isSameStyleActive: editor.isActive(listNodeName, { listStyleType: numberingType }),
      isInsideListItem: editor.isActive('listItem'),
      isCursorOnly,
      isBlock: state.selection.$from.parent.isTextblock,
      isMixedSelection,
    };

    const caseKey = determineListAction(activeType, listType, flags, editor);

    switch (caseKey) {
      case 'update':
        updateListStyle();
        break;
      case 'nested':
        updateNestedListStyle(editor, numberingType);
        break;
      case 'flatten':
        flattenList(editor, listNodeName, numberingType, fontSize);
        break;
      case 'toggle':
      case 'switch':
      default:
        toggleList();
        break;
    }
  };

  return (
    <>
      <EditorMenuDropdown
        placement='bottom'
        element={
          <EditorMenuButton
            className={isBulletList(editor) ? 'is-active' : ''}
            data-testid='unordered-btn'
            disabled={selectionHasRestrictedNodes()}
          >
            <Tooltip direction='bottom' info='Unordered list'>
              <IconDiv enableIcon={selectionHasRestrictedNodes()}>
                <UnorderedListButton />
              </IconDiv>
            </Tooltip>
          </EditorMenuButton>
        }
      >
        <Tooltip info='Disc'>
          <EditorMenuDropdownButton
            onClick={() => setList('bullet', 'disc')}
            data-testid='list-disc-btn'
          >
            <DiscListButton />
          </EditorMenuDropdownButton>
        </Tooltip>
        <Tooltip info='Circle'>
          <EditorMenuDropdownButton
            onClick={() => setList('bullet', 'circle')}
            data-testid='list-circle-btn'
          >
            <CircleListButton />
          </EditorMenuDropdownButton>
        </Tooltip>
        <Tooltip info='Square'>
          <EditorMenuDropdownButton
            onClick={() => setList('bullet', 'square')}
            data-testid='list-square-btn'
          >
            <SquareListButton />
          </EditorMenuDropdownButton>
        </Tooltip>
      </EditorMenuDropdown>

      <EditorMenuDropdown
        placement='bottom'
        element={
          <EditorMenuButton
            className={isOrderedList(editor) ? 'is-active' : ''}
            data-testid='ordered-btn'
            disabled={selectionHasRestrictedNodes()}
          >
            <Tooltip direction='bottom' info='Ordered list'>
              <IconDiv enableIcon={selectionHasRestrictedNodes()}>
                <OrderedListButton />
              </IconDiv>
            </Tooltip>
          </EditorMenuButton>
        }
      >
        <Tooltip info='Numbers'>
          <EditorMenuDropdownButton
            onClick={() => setList('ordered', '1')}
            data-testid='list-number-btn'
          >
            <NumberListButton />
          </EditorMenuDropdownButton>
        </Tooltip>
        <Tooltip info='Alphabet'>
          <EditorMenuDropdownButton
            onClick={() => setList('ordered', 'lower-alpha')}
            data-testid='list-alphabet-btn'
          >
            <AlphabetListButton />
          </EditorMenuDropdownButton>
        </Tooltip>
        <Tooltip info='Roman'>
          <EditorMenuDropdownButton
            onClick={() => setList('ordered', 'lower-roman')}
            data-testid='list-roman-btn'
          >
            <RomanListButton />
          </EditorMenuDropdownButton>
        </Tooltip>
      </EditorMenuDropdown>
    </>
  );
}
